import { GoalThreshold } from '../../coaching-base/config';
import { CommitmentFocusSummarySetDto } from '../../coaching-base/general';
import { GoalsCollection } from '../../coaching-commitment/goals-collection.model';

import {
    makePerformerColumns as makeFocusesForPerformerGridColumns,
    makeTeamColumns as makeFocusesForTeamGridColumns,
} from './focus-history/focus-history.function';
import { makeColumns as makeFocusSelectionColumns } from './focus-selection/focus-selection.function';
import { makeColumns as makeLoadGoalsColumns } from './load-goals/load-goals.function';
import { makeColumns as makeMetricSelectionColumns } from './metric-selection/metric-selection.function';
import {
    defaultPerformerSortDefs as defaultFocusHistoryPerformerGridSortDefs,
    defaultTeamSortDefs as defaultFocusHistoryTeamGridSortDefs,
    FocusHistoryGrid,
} from './model/focus-history.grid.model';
import { FocusSelectorBehaviourModel } from './model/focus-selector-behaviour.model';
import { FocusSelectorFocusForPerformer } from './model/focus-selector-focus-for-performer.model';
import { FocusSelectorFocusForTeam } from './model/focus-selector-focus-for-team.model';
import {
    defaultSortDefs as defaultFocusSelectionSortDefs,
    FocusSelectionGrid,
} from './model/focus-selector-focus-selection.grid.model';
import { FocusSelectorFocusModel } from './model/focus-selector-focus.model';
import { FocusSelectorGoalModel } from './model/focus-selector-goal.model';
import {
    defaultSortDefs as defaultLoadGoalsSortDefs,
    LoadGoalsGrid,
} from './model/focus-selector-load-goals.grid.model';
import {
    defaultSortDefs as defaultMetricSortDefs,
    MetricGrid,
} from './model/focus-selector-metric-selection.grid.model';
import { FocusSelectorMetricModel } from './model/focus-selector-metric.model';
import { FocusSelectorTriggerSource } from './model/focus-selector-trigger-source.enum';
import { LoadGoalsSelectedGoals } from './model/load-goals-selected.model';

export interface FocusSelectorState {
    pending: {
        initMain?: boolean;
    };

    performerId?: number;

    activeCommitmentGoalMetricIds: number[];

    focusSummarySet?: CommitmentFocusSummarySetDto;
    triggerSource?: FocusSelectorTriggerSource;
    goalThresholds?: GoalThreshold[];

    coachableMetrics?: FocusSelectorMetricModel[];
    activeMetric?: FocusSelectorMetricModel;
    initSelectedMetricIds?: number[];

    activeFocusesForPerformer?: FocusSelectorFocusForPerformer[];
    recentlyClosedFocusesForPerformer?: FocusSelectorFocusForPerformer[];
    otherFocusesForPerformer?: FocusSelectorFocusForPerformer[];
    activeFocusesForTeam?: FocusSelectorFocusForTeam[];
    recentlyClosedFocusesForTeam?: FocusSelectorFocusForTeam[];

    behaviours?: FocusSelectorBehaviourModel[];
    selectedFocus: FocusSelectorFocusModel;

    goals?: FocusSelectorGoalModel[];
    selectedGoals: LoadGoalsSelectedGoals;
    goalsCollection?: GoalsCollection;

    activeFocusesPerformerGrid: FocusHistoryGrid<FocusSelectorFocusForPerformer>;
    recentlyClosedFocusesPerformerGrid: FocusHistoryGrid<FocusSelectorFocusForPerformer>;
    activeFocusesTeamGrid: FocusHistoryGrid<FocusSelectorFocusForTeam>;
    recentlyClosedFocusesTeamGrid: FocusHistoryGrid<FocusSelectorFocusForTeam>;
    metricSelectionGrid: MetricGrid;
    focusSelectionGrid: FocusSelectionGrid;
    loadGoalsGrid: LoadGoalsGrid;
}

export const defaultState = (): FocusSelectorState => {
    return {
        pending: {},

        activeCommitmentGoalMetricIds: [],

        selectedGoals: {},

        selectedFocus: {
            behaviour: {
                id: null,
                name: '',
            },

            subBehaviour: {
                id: null,
                name: '',
            },
        },

        metricSelectionGrid: {
            columns: makeMetricSelectionColumns(),
            rows: [],
            sortDefs: defaultMetricSortDefs,
            defaultSortDefs: defaultMetricSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeMetricSelectionColumns(),
                defaultRenderFn: null,
            },
        },

        focusSelectionGrid: {
            columns: makeFocusSelectionColumns(),
            rows: [],
            sortDefs: defaultFocusSelectionSortDefs,
            defaultSortDefs: defaultFocusSelectionSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeFocusSelectionColumns(),
                defaultRenderFn: null,
            },
        },

        activeFocusesPerformerGrid: {
            columns: makeFocusesForPerformerGridColumns(),
            rows: [],
            sortDefs: defaultFocusHistoryPerformerGridSortDefs,
            defaultSortDefs: defaultFocusHistoryPerformerGridSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeFocusesForPerformerGridColumns(),
                defaultRenderFn: null,
            },
        },

        recentlyClosedFocusesPerformerGrid: {
            columns: makeFocusesForPerformerGridColumns(),
            rows: [],
            sortDefs: defaultFocusHistoryPerformerGridSortDefs,
            defaultSortDefs: defaultFocusHistoryPerformerGridSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeFocusesForPerformerGridColumns(),
                defaultRenderFn: null,
            },
        },

        activeFocusesTeamGrid: {
            columns: makeFocusesForTeamGridColumns(),
            rows: [],
            sortDefs: defaultFocusHistoryTeamGridSortDefs,
            defaultSortDefs: defaultFocusHistoryTeamGridSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeFocusesForTeamGridColumns(),
                defaultRenderFn: null,
            },
        },

        recentlyClosedFocusesTeamGrid: {
            columns: makeFocusesForTeamGridColumns(),
            rows: [],
            sortDefs: defaultFocusHistoryTeamGridSortDefs,
            defaultSortDefs: defaultFocusHistoryTeamGridSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeFocusesForTeamGridColumns(),
                defaultRenderFn: null,
            },
        },

        loadGoalsGrid: {
            columns: makeLoadGoalsColumns(),
            rows: [],
            sortDefs: defaultLoadGoalsSortDefs,
            defaultSortDefs: defaultLoadGoalsSortDefs,
            gridRowsTop: [],
            rendererOpts: {
                columns: makeLoadGoalsColumns(),
                defaultRenderFn: null,
            },
            selection: {
                goals: [],
                projectionEvalCol: {
                    metricId: null,
                    range: null,
                },
            },
        },
    };
};
